/**
 * Chart tokens resolved from CSS custom properties (UX-006 §4).
 * Colours come from @solarkey/tokens — never hardcoded. Resolved at runtime
 * so charts follow the live theme (light/dark).
 */

function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export interface ChartTheme {
  measured: string;
  reference: string;
  peer: string;
  band: string;
  bandEdge: string;
  fill: string;
  grid: string;
  axis: string;
  selected: string;
  escapeeLow: string;
  escapeeHigh: string;
  marker: string;
  bar: string;
  barMarker: string;
  ramp: string[];
  text: string;
  textMuted: string;
  /** The ground a label sits on when it has to be legible OVER the plot. */
  surface: string;
  /** That label's edge, so it reads as a chip and not as stray text. */
  border: string;
  /** Source palette (UX-006 §4): a colour names the same source everywhere. */
  source: Record<string, string>;
}

export function chartTheme(): ChartTheme {
  return {
    measured: cssVar("--sk-chart-measured", "#0A0A0F"),
    reference: cssVar("--sk-chart-reference", "#4B5563"),
    peer: cssVar("--sk-chart-peer", "rgba(107,114,128,0.55)"),
    band: cssVar("--sk-chart-band", "#EFF1F5"),
    bandEdge: cssVar("--sk-chart-band-edge", "rgba(10,10,15,0.14)"),
    fill: cssVar("--sk-chart-fill", "#EFF1F5"),
    grid: cssVar("--sk-chart-grid", "rgba(10,10,15,0.08)"),
    axis: cssVar("--sk-chart-axis", "rgba(10,10,15,0.14)"),
    selected: cssVar("--sk-chart-selected", "#2563EB"),
    escapeeLow: cssVar("--sk-chart-escapee-low", "#7F1D1D"),
    escapeeHigh: cssVar("--sk-chart-escapee-high", "#C2410C"),
    marker: cssVar("--sk-chart-marker", "#6B7280"),
    bar: cssVar("--sk-chart-bar", "#4B5563"),
    barMarker: cssVar("--sk-chart-bar-marker", "#0A0A0F"),
    ramp: [
      cssVar("--sk-chart-ramp-0", "#EFF1F5"),
      cssVar("--sk-chart-ramp-1", "#E6E9EF"),
      cssVar("--sk-chart-ramp-2", "#D5DAE3"),
      cssVar("--sk-chart-ramp-3", "#BFC7D3"),
      cssVar("--sk-chart-ramp-4", "#94A0B4"),
      cssVar("--sk-chart-ramp-5", "#5B6B84"),
    ],
    text: cssVar("--sk-ink-1", "#0A0A0F"),
    textMuted: cssVar("--sk-ink-3", "#6B7280"),
    // Falls back like every other entry here, so a chart is legible on its own
    // and does not depend on a stylesheet from another package being present.
    surface: cssVar("--sk-color-surface-0", "#FFFFFF"),
    border: cssVar("--sk-color-border-1", "rgba(10,10,15,0.14)"),
    source: {
      solar: cssVar("--sk-color-source-solar", "#B8860B"),
      grid: cssVar("--sk-color-source-grid", "#334155"),
      storage: cssVar("--sk-color-source-storage", "#7C3AED"),
      diesel: cssVar("--sk-color-source-diesel", "#9A3412"),
      load: cssVar("--sk-color-source-load", "#0A0A0F"),
    },
  };
}
